import axios from 'axios';

/**
 * Owns the access/refresh token pair.
 *
 * This module deliberately imports nothing from the app (no stores, no apiClient) so
 * that the axios interceptor, the socket client, and the file-download helpers can all
 * share one refresh path without an import cycle. Consumers subscribe via onTokenChange.
 */

// A bare client with no interceptors — refreshing through apiClient would recurse
// back into the 401 handler that called us.
const refreshClient = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Refresh a little before the token actually dies, so an in-flight request can't
// arrive at the server just after expiry.
const EXPIRY_SKEW_MS = 60000;

let refreshPromise = null;
const listeners = new Set();

export function getAccessToken() {
  return localStorage.getItem('accessToken');
}

export function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function onTokenChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function emit(token) {
  listeners.forEach((cb) => {
    try {
      cb(token);
    } catch (err) {
      console.error('[tokenManager] listener threw:', err);
    }
  });
}

function storeTokens(accessToken, refreshToken) {
  localStorage.setItem('accessToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  emit(accessToken);
}

export function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  emit(null);
}

/** Read the `exp` claim without pulling in a JWT library. */
function getExpiry(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json);
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True if the token is missing, already expired, or about to expire. */
export function isExpiring(token = getAccessToken(), skewMs = EXPIRY_SKEW_MS) {
  if (!token) return true;
  const exp = getExpiry(token);
  // Unparseable token: let the server be the judge rather than forcing a refresh loop.
  if (!exp) return false;
  return Date.now() >= exp - skewMs;
}

/**
 * Exchange the refresh token for a new pair. Single-flight: concurrent callers
 * (a burst of 401s, the socket, and a file click all at once) share one request,
 * which matters because the server rotates the refresh token on every use — firing
 * two of these in parallel would invalidate one of them.
 */
export function refreshTokens() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }

  refreshPromise = refreshClient
    .post('/auth/refresh', { refreshToken })
    .then((res) => {
      // No response interceptor on this client, so unwrap the envelope by hand.
      const body = res.data;
      const data = body && body.success !== undefined ? body.data : body;

      if (!data || !data.accessToken) {
        throw new Error('Malformed refresh response');
      }

      storeTokens(data.accessToken, data.refreshToken);
      return data.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Return a token that is valid right now, refreshing first if the current one is
 * expired or close to it. Falls back to the existing token on failure and lets the
 * caller's request produce the authoritative 401.
 */
export async function ensureFreshToken() {
  const token = getAccessToken();
  if (!isExpiring(token)) return token;

  try {
    return await refreshTokens();
  } catch {
    return token;
  }
}

/**
 * Distinguishes "the refresh token is genuinely dead" (log the user out) from
 * "the refresh call didn't get through" — a 429 from the rate limiter, a 502, or an
 * offline blip. Only the former should bounce someone to the login page.
 */
export function isFatalRefreshError(error) {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return true;
  // No refresh token at all — nothing to recover with.
  if (!getRefreshToken()) return true;
  return false;
}
